<template>
  <div style="padding: 1rem 2rem 2rem">
    <div class="add-user-header-div">
      <div class="add-user-title">
        <div class="action-provider-settings-title">
          <h4 style="width: 100%">Custom Metric</h4>
        </div>
      </div>
      <div style="width: 200px; text-align: right">
        <v-btn variant="flat" color="success" @click="editMetric(-1)">
          <v-icon start size="18">md:add</v-icon>Add New Metric
        </v-btn>
      </div>
    </div>
    <v-table class="rf-settings-table-data">
      <thead>
        <tr>
          <th>Metric Name</th>
          <th>Model Type (Segment/Path)</th>
          <th />
        </tr>
      </thead>
      <tbody v-if="customMetrics.customMetrics.value?.length">
        <tr v-for="(customMetric, index) in customMetrics.customMetrics.value" :key="index">
          <td>{{ customMetric.metric_key }}</td>
          <td>{{ customMetric.model_type }}</td>
          <td align="right">
            <div class="inline-flex items-center gap-3">
              <RfButton
                icon="md:edit"
                variant="plain"
                density="compact"
                size="small"
                color="accent"
                @click="editMetric(index)"
              />
              <RfButton
                icon="md:clear"
                variant="plain"
                density="compact"
                size="small"
                color="error"
                @click="openDeleteDialog(customMetric)"
              />
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td align="center" colspan="3">
            <div class="settings-no-data-message">Nothing to show</div>
          </td>
        </tr>
      </tbody>
    </v-table>
    <v-dialog v-model="addDialog" persistent width="700">
      <v-card class="rf-custom-web-card-wrapper">
        <v-card-title>Add New Metric</v-card-title>
        <v-card-text style="margin-top: 1rem">
          <v-text-field
            v-model="editing.metric_key"
            persistentPlaceholder
            label="Metric Key"
            placeholder="e.g. failed_goals, timeout_users (impressions, users, uimpressions, goals not allowed)"
            variant="outlined"
            density="compact"
          ></v-text-field>
          <v-select
            v-model="editing.model_type"
            persistentPlaceholder
            label="Model Type"
            placeholder="None"
            :items="['segment', 'path', 'experiment', 'action_group', 'app']"
            chips
            variant="outlined"
            density="compact"
            bgColor="#ffffff"
            persistentHint
          ></v-select>
          <p>Add json from Kibana here.</p>
          <component
            :is="codeEditor.CodeEditor"
            v-if="addDialog"
            v-model="editing.query"
            v-bind="{ ...codeEditor.props, languages: codeEditor.languages('json') }"
          ></component>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            style="width: 160px"
            variant="flat"
            color="accent"
            :disabled="!submitEnabled"
            @click="submitAction"
          >
            {{ editingIndex >= 0 ? "Update" : "Create" }}
          </v-btn>
          <v-btn style="width: 120px" class="cancel-btn" variant="outlined" @click="closeDialog"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <RfBaseModal
      v-if="deleteCustomMetric"
      ref="deleteModal"
      :modelValue="true"
      message="This cannot be undone."
      width="580"
      errorText="Delete"
      @close="closeDeleteDialog"
      @error="deleteMetric"
    >
      <template #title>
        Are you sure you want to delete
        <span class="font-bold whitespace-pre"> {{ deleteCustomMetric.metric_key }} </span>?
      </template>
    </RfBaseModal>
  </div>
</template>

<script setup>
import { useToastsStore } from "@/pinia/toastsStore";
import { useCustomMetrics } from "@/utils/composables/useCustomMetrics";
import { computed, onMounted, ref } from "vue";
import { cloneDeep } from "lodash-es";
import RfButton from "@/components/buttons/RfButton.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import { useCodeEditor } from "@/utils/composables/useCodeEditor";

const codeEditor = useCodeEditor();
const toastsStore = useToastsStore();
const customMetrics = useCustomMetrics();

const addDialog = ref(false);
const editing = ref({});
const editingIndex = ref(-1);
const deleteDialog = ref(false);
const deleteCustomMetric = ref(null);

const submitEnabled = computed(
  () => !!(editing.value.model_type && editing.value.metric_key && editing.value.query),
);

const defaultMetric = { model_type: null, metric_key: null, query: null };

const editMetric = index => {
  if (index >= 0) {
    editing.value = cloneDeep(customMetrics.customMetrics.value[index]);
  } else {
    editing.value = { ...defaultMetric };
  }
  editingIndex.value = index;
  addDialog.value = true;
};

const closeDialog = () => {
  editingIndex.value = -1;
  addDialog.value = false;
  editing.value = { ...defaultMetric };
};

const openDeleteDialog = customMetric => {
  deleteCustomMetric.value = customMetric;
  deleteDialog.value = true;
};

const closeDeleteDialog = () => {
  deleteCustomMetric.value = null;
  deleteDialog.value = false;
};

const deleteMetric = async () => {
  await customMetrics.deleteCustomMetric(deleteCustomMetric.value);
  toastsStore.create({ type: "success", body: "Custom metric deleted" });
  closeDeleteDialog();
};

const submitAction = () => {
  if (editingIndex.value >= 0) {
    customMetrics.updateCustomMetric(editing.value);
    toastsStore.create({ type: "success", body: "Custom metric updated" });
  } else {
    customMetrics.createCustomMetric(editing.value);
    toastsStore.create({ type: "success", body: "Custom metric created" });
  }
  closeDialog();
};

onMounted(customMetrics.getCustomMetrics);
</script>
